// components/list/factoryMentionOptions.ts
import type { FactoryPersonaDto, FactoryObjectDto } from '#types/types';
import type { FactoryFolder } from '#lib/list/factoryProfile';

type MentionOption = {
  id: string; handle: string; name: string; thumbnailUrl?: string;
  category: 'Personas' | 'Objects' | 'Archetypes' | 'Folders' | 'Outputs'; unavailable?: string;
};

/** Keep category order and availability independent of asynchronous picker requests. */
export function factoryMentionOptions(archetypes: unknown, personas: FactoryPersonaDto[], query: string, outputs: readonly MentionOption[] = [], objects: readonly FactoryObjectDto[] = [], folders: readonly FactoryFolder[] = []): MentionOption[] {
  const needle = query.trim().toLowerCase();
  const matches = (item: { handle: string; name: string }) => item.handle.toLowerCase() !== 'last' && (!needle || item.handle.toLowerCase().includes(needle) || item.name.toLowerCase().includes(needle));
  const characters: MentionOption[] = personas.filter(matches).slice(0, 8).map(persona => ({
    id: persona.id, handle: persona.handle, name: persona.name, category: 'Personas',
    thumbnailUrl: persona.referenceImages[0]?.thumbnailUrl,
    ...(persona.status !== 'ready' ? { unavailable: persona.status === 'generating' ? 'Building reference sheet' : 'Reference sheet needed' } : {}),
  }));
  const concepts: MentionOption[] = (Array.isArray(archetypes) ? archetypes : []).flatMap((value): MentionOption[] => {
    if (!value || typeof value !== 'object') return [];
    const item = value as Record<string, unknown>;
    return typeof item.id === 'string' && typeof item.handle === 'string' && typeof item.name === 'string'
      ? [{ id: item.id, handle: item.handle, name: item.name, category: 'Archetypes', ...(typeof item.thumbnailUrl === 'string' ? { thumbnailUrl: item.thumbnailUrl } : {}) }]
      : [];
  }).filter(matches).slice(0, 8);
  const products: MentionOption[] = objects.map(object => ({
    id: object.id, handle: 'object:' + object.handle, name: object.name, category: 'Objects' as const,
    thumbnailUrl: object.referenceImages[0]?.thumbnailUrl,
    ...(object.status !== 'ready' || object.sheet?.contentRevision !== object.contentRevision ? { unavailable: object.status === 'generating' ? 'Building object panel' : 'Object panel needed' } : {}),
  })).filter(matches).slice(0, 8);
  const libraries: MentionOption[] = folders.map(folder => ({ id: folder.id, handle: `folder:${folder.name.toLowerCase().replace(/\s+/g, '-')}`, name: folder.name,
    category: 'Folders' as const,
  })).filter(item => matches(item) || item.handle.replace(/[^a-z0-9]/g, '').includes(needle.replace(/[^a-z0-9]/g, '')));
  return [...characters, ...products, ...concepts, ...libraries, ...outputs.filter(matches)];
}
